import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  findVehiclePricingSubstep,
  findVehiclePricingSubstepIds,
} from "../../../shared/utils/onboardingProgressUtil";
import styles from "./OnboardingVehiclePricing.module.scss";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";

const OnboardingVehiclePricing = ({ user, progressRequest, progressSteps }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [completionError, setCompletionError] = useState(false);
  const [completionSuccess, setCompletionSuccess] = useState(false);

  const updatedBy =
    user?.userId != null && String(user.userId).trim()
      ? String(user.userId).trim()
      : "";

  const vehiclePricingSubstep = useMemo(
    () => findVehiclePricingSubstep(progressSteps),
    [progressSteps]
  );

  const vehiclePricingSubstepIds = useMemo(
    () => findVehiclePricingSubstepIds(progressSteps),
    [progressSteps]
  );

  const vehiclePricingAlreadyCompleted =
    vehiclePricingSubstep?.status?.toLowerCase() === "completed";

  const completeVehiclePricingMutation = useMutation({
    mutationFn: ({ dealerCommonId, stepId, substepId, updatedBy: userId }) =>
      DealerOnboardingClient.saveSubstep(dealerCommonId, {
        stepId,
        substepId,
        status: "completed",
        updatedBy: userId,
      }),

    onSuccess: () => {
      setCompletionError(false);
      setCompletionSuccess(true);

      queryClient.invalidateQueries({
        queryKey: [
          DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
          progressRequest?.dealerCommonId,
        ],
        exact: true,
        refetchType: "active",
      });
    },

    onError: () => {
      setCompletionSuccess(false);
      setCompletionError(true);
    },
  });

  const handleSetPricingClick = () => {
    const dealerCommonId = progressRequest?.dealerCommonId;
    const stepId = vehiclePricingSubstepIds?.stepId;
    const substepId = vehiclePricingSubstepIds?.substepId;

    if (
      completeVehiclePricingMutation.isPending ||
      vehiclePricingAlreadyCompleted
    ) {
      return;
    }

    if (!dealerCommonId || !updatedBy || stepId == null || substepId == null) {
      setCompletionSuccess(false);
      setCompletionError(true);
      return;
    }

    setCompletionError(false);
    setCompletionSuccess(false);

    completeVehiclePricingMutation.mutate({
      dealerCommonId,
      stepId,
      substepId,
      updatedBy,
    });
  };

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="vehicle-pricing-title" className="tw-mb-6">
        {t("DealerOnboarding.content.vehiclePricing.title", "Vehicle Pricing")}
      </OnboardingPageTitle>

      <Box className={styles.contentContainer}>
        {completionSuccess && (
          <EMPConfirmationBanner
            variant="success"
            primaryText={t(
              "DealerOnboarding.content.vehiclePricing.stepCompleted",
              "Your preferences have been saved"
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.vehiclePricing.closeSuccessBanner",
              "Close confirmation banner"
            )}
            testId="vehicle-pricing-completion-success"
          />
        )}

        {completionError && (
          <EMPConfirmationBanner
            variant="error"
            primaryText={t(
              "DealerOnboarding.content.vehiclePricing.stepCompletionError",
              "Unable to complete this step. Please try again."
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.vehiclePricing.closeErrorBanner",
              "Close error banner"
            )}
            testId="vehicle-pricing-completion-error"
          />
        )}

        <Typography component="div" className={styles.bodyText}>
          <p className={styles.copyParagraph}>
            {t(
              "DealerOnboarding.content.vehiclePricing.informationalCopy.intro",
              "Ford.com and Lincoln.com display the vehicle pricing you configure in Vehicle Locator Plus (VL+). To set your dealer pricing, you can take one of the following actions in VL+:"
            )}
          </p>

          <ul className={styles.pricingActionList}>
            <li>
              <strong className={styles.boldBodyText}>
                {t(
                  "DealerOnboarding.content.vehiclePricing.informationalCopy.directRuleTitle",
                  "Input a pricing rule directly in VL+:"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.vehiclePricing.informationalCopy.directRuleDescription",
                'Set pricing rules from the Brand/Model level down to an individual VIN. This rule will automatically update the "Dealer adjustment" field on your vehicle listings.'
              )}
            </li>

            <li>
              <strong className={styles.boldBodyText}>
                {t(
                  "DealerOnboarding.content.vehiclePricing.informationalCopy.syndicateTitle",
                  "Syndicate pricing from an external source:"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.vehiclePricing.informationalCopy.syndicateDescription",
                "Import prices directly from your inventory management tool or dealership website. VL+ currently supports syndication with vAuto Conquest and Dealer.com."
              )}
            </li>
          </ul>

          <p className={styles.copyParagraph}>
            {t(
              "DealerOnboarding.content.vehiclePricing.informationalCopy.outro",
              "This dealer-configured price will display prior to any incentives, rebates, taxes, or fees. If you do not set a pricing rule or syndicate pricing in VL+, the vehicle will default to MSRP with no dealer adjustment."
            )}
          </p>
        </Typography>

        <Box className={styles.checklistRow}>
          <svg
            className={styles.checklistIcon}
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="10"
            viewBox="0 0 14 10"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.33333 7.69233C3.88544 7.69233 4.42261 7.75495 4.94483 7.88017C5.46706 8.00539 5.98544 8.20178 6.5 8.46933V1.91533C6.03167 1.61022 5.52483 1.38139 4.9795 1.22883C4.43417 1.07628 3.88544 1 3.33333 1C2.912 1 2.50194 1.03783 2.10317 1.1135C1.7045 1.18917 1.33678 1.30906 1 1.47317V8.10517C1.35044 7.97183 1.72694 7.86967 2.1295 7.79867C2.53206 7.72778 2.93333 7.69233 3.33333 7.69233ZM7.5 8.46933C8.01456 8.20178 8.53294 8.00539 9.05517 7.88017C9.57739 7.75495 10.1146 7.69233 10.6667 7.69233C11.0667 7.69233 11.4679 7.72778 11.8705 7.79867C12.2731 7.86967 12.6496 7.97183 13 8.10517V1.47317C12.6632 1.30906 12.2955 1.18917 11.8968 1.1135C11.4981 1.03783 11.088 1 10.6667 1C10.1146 1 9.56583 1.07628 9.0205 1.22883C8.47517 1.38139 7.96833 1.61022 7.5 1.91533V8.46933ZM7 9.93583C6.46244 9.53928 5.88256 9.23289 5.26033 9.01667C4.63811 8.80045 3.99578 8.69233 3.33333 8.69233C2.76578 8.69233 2.18867 8.78633 1.602 8.97433C1.01522 9.16233 0.481222 9.43628 0 9.79617V0.886001C0.433333 0.603889 0.947444 0.385722 1.54233 0.2315C2.13722 0.0771669 2.73422 0 3.33333 0C3.982 0 4.61578 0.088667 5.23467 0.266C5.85344 0.443445 6.44189 0.705223 7 1.05133C7.55811 0.705223 8.14656 0.443445 8.76533 0.266C9.38422 0.088667 10.018 0 10.6667 0C11.2658 0 11.8617 0.0771669 12.4545 0.2315C13.0472 0.385722 13.5623 0.603889 14 0.886001V9.79617C13.5257 9.43628 12.9934 9.16233 12.4032 8.97433C11.8131 8.78633 11.2342 8.69233 10.6667 8.69233C10.0042 8.69233 9.36189 8.80045 8.73967 9.01667C8.11744 9.23289 7.53756 9.53928 7 9.93583ZM8.29483 3.51033V2.63333C8.65728 2.465 9.03656 2.33872 9.43267 2.2545C9.82889 2.17028 10.2402 2.12817 10.6667 2.12817C10.9342 2.12817 11.1941 2.14722 11.4462 2.18533C11.6983 2.22333 11.9513 2.27356 12.2052 2.336V3.17183C11.9556 3.08461 11.7075 3.02133 11.4608 2.982C11.2143 2.94267 10.9496 2.923 10.6667 2.923C10.2402 2.923 9.82717 2.97367 9.4275 3.075C9.02795 3.17633 8.65039 3.32144 8.29483 3.51033ZM8.29483 7.16417V6.27433C8.64872 6.106 9.02695 5.97972 9.4295 5.8955C9.83206 5.81139 10.2444 5.76933 10.6667 5.76933C10.9342 5.76933 11.1941 5.78833 11.4462 5.82633C11.6983 5.86433 11.9513 5.91456 12.2052 5.977V6.81283C11.9556 6.72561 11.7075 6.66233 11.4608 6.623C11.2143 6.58378 10.9496 6.56417 10.6667 6.56417C10.2402 6.56417 9.82717 6.61522 9.4275 6.71733C9.02795 6.81944 8.65039 6.96839 8.29483 7.16417ZM8.29483 5.34367V4.45383C8.65728 4.2855 9.03656 4.15922 9.43267 4.075C9.82889 3.99078 10.2402 3.94867 10.6667 3.94867C10.9342 3.94867 11.1941 3.96772 11.4462 4.00583C11.6983 4.04383 11.9513 4.09406 12.2052 4.1565V4.99233C11.9556 4.90511 11.7075 4.84183 11.4608 4.8025C11.2143 4.76328 10.9496 4.74367 10.6667 4.74367C10.2402 4.74367 9.82717 4.79645 9.4275 4.902C9.02795 5.00756 8.65039 5.15478 8.29483 5.34367Z"
              fill="currentColor"
            />
          </svg>

          <Typography component="p" className={styles.checklistText}>
            {t(
              "DealerOnboarding.content.vehiclePricing.checklistPrefix",
              "Need help? "
            )}

            <a
              className={styles.checklistLink}
              href={t(
                "DealerOnboarding.content.vehiclePricing.checklistLink",
                "#"
              )}
            >
              {t(
                "DealerOnboarding.content.vehiclePricing.checklistLabel",
                "View Vehicle Pricing Checklist"
              )}
            </a>
          </Typography>
        </Box>

        <Box className={styles.setPricingButtonContainer}>
          <EMPButton
            variant="contained"
            isFord
            testId="vehicle-pricing-set-pricing-button"
            rightIcon={<KeyboardArrowRight aria-hidden="true" />}
            onClick={handleSetPricingClick}
            href={t(
              "DealerOnboarding.content.vehiclePricing.vlPlusLink",
              "https://www.vlplus.forddirectservices.com/"
            )}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              boxSizing: "border-box",
              display: "inline-flex",
              flex: "0 0 auto",
              width: "142px",
              minWidth: "142px",
              height: "40px",
              padding: "8px 12px",
              alignItems: "center",
              justifyContent: "flex-start",
              border: "none",
              borderRadius: "400px",
              backgroundColor: "var(--button-primary-onlight-default, #066FEF)",
              color: "var(--button-primary-onlight-content, #FFF)",
              fontFamily:
                'var(--body-1-regular-font-family, "Lincoln Proxima Nova")',
              fontSize: "var(--body-1-regular-font-size, 16px)",
              fontStyle: "normal",
              fontWeight: "var(--body-1-regular-font-weight, 700)",
              lineHeight: "var(--body-1-regular-line-height, 24px)",
              textAlign: "left",
              textTransform: "none",
              whiteSpace: "nowrap",
              "& .MuiButton-endIcon": {
                display: "inline-flex",
                flex: "0 0 24px",
                width: "24px",
                height: "24px",
                margin: "0 0 0 auto",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiButton-endIcon svg": {
                width: "24px",
                height: "24px",
                color: "var(--button-primary-onlight-content, #FFF)",
              },
              "&:hover": {
                border: "none",
                backgroundColor:
                  "var(--button-primary-onlight-default, #066FEF)",
              },
            }}
          >
            <span className={styles.setPricingButtonLabel}>
              {t(
                "DealerOnboarding.content.vehiclePricing.setPricingButtonLabel",
                "Set Pricing"
              )}
            </span>
          </EMPButton>
        </Box>
      </Box>
    </div>
  );
};

OnboardingVehiclePricing.propTypes = {
  user: PropTypes.object,
  progressRequest: PropTypes.shape({
    dealerCommonId: PropTypes.string,
    dealerInfo: PropTypes.object,
  }),
  progressSteps: PropTypes.array,
};

export default OnboardingVehiclePricing;
